// Pure helpers for role resolution. Split out of RoleProvider.tsx so that
// file exports only the component (satisfies react-refresh).

import { translateRoleParam } from "@/session/roleShortCodes";
import type { RoleStatus } from "./context";

/**
 * Read and translate `?as=<code>` from a URL search string.
 *
 * Deterministic — unit-testable without a Router. Returns `null` if the
 * value doesn't translate to a known canonical role name. The caller is
 * responsible for verifying the returned role is in `distinct_roles`
 * before setting `sessionStorage.currentRole` (DESIGN.md §9: "never pick
 * something the user doesn't hold").
 */
export function readAsParam(search: string): string | null {
  if (!search) return null;
  const params = new URLSearchParams(search);
  const raw = params.get("as");
  return translateRoleParam(raw);
}

export interface ResolvedRole {
  role: string | null;
  status: RoleStatus;
}

/**
 * Resolve the initial active role from session + URL + stored state.
 *
 * Priority order per task 42 / DESIGN.md §9:
 *   1. 42.4: `?as=<code>` if it translates to a role in `distinct_roles`.
 *   2. Stored `sessionStorage.currentRole` if still a member of
 *      `distinct_roles` (honors a prior in-tab pick).
 *   3. 42.1: auto-select when `distinct_roles.length === 1`.
 *   4. 42.2: fall through to `status: "pending"` — picker required.
 *
 * Pure function; deterministic in its inputs.
 */
export function resolveInitialRole(params: {
  distinctRoles: readonly string[];
  asParam: string | null;
  storedRole: string | null;
}): ResolvedRole {
  const { distinctRoles, asParam, storedRole } = params;

  if (distinctRoles.length === 0) {
    return { role: null, status: "none" };
  }

  if (asParam !== null && distinctRoles.includes(asParam)) {
    return {
      role: asParam,
      status: distinctRoles.length === 1 ? "single" : "selected",
    };
  }

  if (storedRole !== null && distinctRoles.includes(storedRole)) {
    return {
      role: storedRole,
      status: distinctRoles.length === 1 ? "single" : "selected",
    };
  }

  if (distinctRoles.length === 1) {
    const sole = distinctRoles[0];
    if (sole !== undefined) return { role: sole, status: "single" };
  }

  return { role: null, status: "pending" };
}
